import { FuelGridContainer, FuelGridItem } from '@frete.com/fuel-react';
import TruckerProfileSkeleton from 'components/Externals/TruckerProfileSkeleton';
import { OutletWrapper } from 'components/OutletWrapper';
import { SkeletonSideBar } from 'components/Skeleton/SkeletonSideBar';
import EmptyProfileRefundableFare from 'components/States/EmptyProfileRefundableFare';
import EmptyProfileWrapperContent from 'components/States/EmptyProfileWrapperContent';
import { SUGGESTED_TAB, TABS, TARRIF_TAB } from 'context/reducers/tabs.type';
import { TTruckerItem } from 'context/reducers/truckers.type';
import { useAppContext } from 'hooks/useAppContext';
import { useAppStates } from 'hooks/useAppStates';
import { useDeviceSize } from 'hooks/useDeviceSize';
import { useFilteredData } from 'hooks/useFilteredData';
import { useLoadTruckers } from 'hooks/useLoadTruckers';
import { useLocalStorage } from 'hooks/useLocalStorage';
import useUser from 'hooks/useUser';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isEmpty } from 'utils/isEmpty';
import Sidebar from './Sidebar';
import * as Styled from './styled';

const TruckerList: FC = () => {
  const {
    state: {
      truckers: { radius, activeDriver },
      searchTruckers: { dataSearch, term, errorSearch },
      tabs: { activeTab },
      refundableFare: { isFetchingRefundableFare },
      deviceSize: { isDesktop },
    },
    dispatch,
  } = useAppContext();

  useDeviceSize(true);

  const { activeTab: getParamsTab } = useParams();

  const { filteredDataSuggested } = useFilteredData();
  const {
    isLoadingFull,
    isEmptySuggested,
    isLoadErrorPartial,
    isLoadErrorFull,
    isEmptySearchDriver,
    isLoadingPartial,
    isEmptyRefundableFare,
  } = useAppStates();

  const getData = () => filteredDataSuggested;
  const { getItemAsJson } = useLocalStorage();

  const { fetchTruckers } = useLoadTruckers();
  const { fetchUser } = useUser();
  const navigate = useNavigate();

  const [dataActiveTab, setDataActiveTab] = useState<TTruckerItem[]>([]);

  const setActiveTab = () => {
    dispatch({ type: 'tab_active_set', payload: TABS[getParamsTab] });
  };

  const firstDataId = (truckers: TTruckerItem[]) => truckers[0]?.person.id || truckers[0]?.vehicle.id;

  const getFirstTrucker = (truckersData: TTruckerItem[]): { data: TTruckerItem; id: string } => ({
    id: firstDataId(truckersData),
    data: truckersData[0],
  });

  const dispatchActiveDriver = (trucker: TTruckerItem, id: string): void => {
    dispatch({
      type: 'truckers_driver_auto_active_set',
      payload: {
        activeDriver: {
          ...trucker,
          id,
        },
      },
    });
  };

  const setActiveDriver = (rawData: TTruckerItem[]): void => {
    const activeDriverParsed = { id: activeDriver?.id, data: activeDriver };
    const visibleTrucker =
      rawData?.[0]?.person?.id === activeDriver?.id ? activeDriverParsed : getFirstTrucker(rawData);

    if (activeTab === SUGGESTED_TAB) {
      dispatchActiveDriver(visibleTrucker?.data, visibleTrucker?.id);

      navigate(visibleTrucker?.id);
    }
  };

  useEffect(() => {
    const currentOverflow = document.body.style.overflowY;
    document.body.style.overflowY = 'hidden';

    const header: HTMLElement = document.querySelector("[data-mobile='header']");

    if (header) {
      header.style.display = 'none';
    }

    return () => {
      document.body.style.overflowY = currentOverflow;
      if (header) {
        header.style.display = 'flex';
      }
    };
  }, []);

  useEffect(() => {
    setActiveTab();
  }, []);

  useEffect(() => {
    const truckerValueId = getItemAsJson('truckerId');

    setDataActiveTab(getData());
    if (truckerValueId) {
      const valueTruckerActive = getData().filter((trucker) => trucker.person.id === truckerValueId);
      setActiveDriver(valueTruckerActive);
    }
  }, [filteredDataSuggested, activeTab]);

  useEffect(() => {
    if (isDesktop && !!dataActiveTab?.length) setActiveDriver(dataActiveTab);
  }, [dataActiveTab]);

  useEffect(() => {
    if (term && !isEmpty(dataSearch) && isDesktop) {
      dispatch({
        type: 'truckers_driver_active_set',
        payload: { activeDriver: { id: dataSearch.trucker.id } },
      });
      navigate(dataSearch.trucker.id);
    }
  }, [dataSearch]);

  useEffect(() => {
    if (!isDesktop && activeDriver) {
      dispatch({
        type: 'truckers_driver_active_set',
        payload: {
          activeDriver: null,
        },
      });
      return;
    }
    if (isDesktop && !!dataActiveTab?.length && !term) {
      setActiveDriver(dataActiveTab);
    }
  }, [isDesktop]);

  useEffect(() => {
    fetchTruckers(radius);
    fetchUser();
  }, []);

  const renderOutletWrapper =
    ((term && !isEmptySearchDriver) || (!term && !isEmptySuggested)) &&
    !(isLoadErrorPartial || isLoadErrorFull) &&
    !isLoadingPartial;

  const renderSkeletonProfile = isLoadingFull || isLoadingPartial || isFetchingRefundableFare;

  const renderEmptyProfile =
    isEmptySuggested || isLoadErrorPartial || isLoadErrorFull || isEmptySearchDriver || !isEmpty(errorSearch);

  const renderEmptyRefundableFareProfile = isEmptyRefundableFare && activeTab === TARRIF_TAB;

  return (
    <Styled.Wrapper>
      <FuelGridContainer id="container">
        <FuelGridItem lg={7} xlg={7} sm={4} md={8}>
          {isLoadingFull ? <SkeletonSideBar isMobile={!isDesktop} /> : <Sidebar />}
        </FuelGridItem>
        <Styled.FuelGridItemProfile lg={5} xlg={5} sm={4} md={8} activeDriver={activeDriver}>
          <Styled.DriverProfileWrapper>
            {renderOutletWrapper && <OutletWrapper />}

            {renderSkeletonProfile && <TruckerProfileSkeleton />}

            {renderEmptyProfile && <EmptyProfileWrapperContent />}

            {renderEmptyRefundableFareProfile && <EmptyProfileRefundableFare />}
          </Styled.DriverProfileWrapper>
        </Styled.FuelGridItemProfile>
      </FuelGridContainer>
    </Styled.Wrapper>
  );
};

export default TruckerList;
